import {
	useEconomicPaymentTermsQuery,
	useApproveEarlyAccessRequestMutation,
} from '@codezo/graphql';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../../components/ui/dialog';
import { Field } from '../../components/ui/field';
import { SelectPopover } from '../../components/ui/select-popover';

export interface EarlyAccessRequestSummary {
	id: string;
	firstName: string;
	lastName: string;
	email: string;
	companyName?: string | null;
}

export function ApproveEarlyAccessDialog({
	request,
	onOpenChange,
	onApproved,
}: {
	request: EarlyAccessRequestSummary | null;
	onOpenChange: (open: boolean) => void;
	onApproved: (inviteToken: string) => void;
}) {
	const open = request !== null;

	const [paymentTermsNumber, setPaymentTermsNumber] = useState('');

	const { data: paymentTermsData, error: paymentTermsError } = useEconomicPaymentTermsQuery({
		fetchPolicy: 'no-cache',
		skip: !open,
	});
	const [approveRequest, { loading }] = useApproveEarlyAccessRequestMutation();

	const paymentTermsOptions = useMemo(
		() =>
			paymentTermsData?.economicPaymentTerms?.map((t) => ({
				value: String(t.paymentTermsNumber),
				label: t.daysOfCredit != null ? `${t.name} (${t.daysOfCredit} days)` : t.name,
			})) ?? [],
		[paymentTermsData]
	);

	const selectedPaymentTerm = useMemo(
		() =>
			paymentTermsData?.economicPaymentTerms?.find(
				(t) => String(t.paymentTermsNumber) === paymentTermsNumber
			) ?? null,
		[paymentTermsData, paymentTermsNumber]
	);

	// e-conomic returns a specific message when the integration isn't set up.
	const paymentTermsUnavailable = Boolean(paymentTermsError);

	// Prefill with the backend-flagged default once, without clobbering a manual pick.
	useEffect(() => {
		if (paymentTermsNumber) return;
		const terms = paymentTermsData?.economicPaymentTerms;
		if (!terms?.length) return;
		const fallback = terms.find((t) => t.isDefault) ?? terms[0];
		setPaymentTermsNumber(String(fallback.paymentTermsNumber));
	}, [paymentTermsData, paymentTermsNumber]);

	const reset = () => setPaymentTermsNumber('');

	const handleApprove = async () => {
		if (!request || !paymentTermsNumber) return;
		try {
			const { data } = await approveRequest({
				variables: {
					input: {
						requestId: request.id,
						economicPaymentTermsNumber: Number(paymentTermsNumber),
						economicPaymentTermsName: selectedPaymentTerm?.name,
					},
				},
			});
			if (data?.approveEarlyAccessRequest) {
				toast.success('Request approved — invite ready');
				reset();
				onApproved(data.approveEarlyAccessRequest.inviteToken);
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to approve request');
		}
	};

	const canApprove = paymentTermsNumber.length > 0 && !paymentTermsUnavailable;

	return (
		<Dialog
			open={open}
			onOpenChange={(next) => {
				if (!next) reset();
				onOpenChange(next);
			}}
		>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Approve request</DialogTitle>
					<DialogDescription>
						{request
							? `Set the payment terms ${request.companyName || `${request.firstName} ${request.lastName}`} will sit under, then send the invite.`
							: null}
					</DialogDescription>
				</DialogHeader>

				<DialogForm onSubmit={handleApprove}>
					<DialogBody>
						<Field
							label="Payment terms"
							required
							error={
								paymentTermsUnavailable
									? 'e-conomic integration is not configured'
									: null
							}
						>
							<SelectPopover
								value={paymentTermsNumber}
								onChange={setPaymentTermsNumber}
								options={paymentTermsOptions}
								placeholder={
									paymentTermsUnavailable ? 'Unavailable' : 'Select payment terms'
								}
							/>
						</Field>
						<p className="mt-2 text-sm text-text-4">
							Applied to the client&rsquo;s e-conomic customer record when they
							complete signup. They never see or pick this.
						</p>
					</DialogBody>

					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => {
								reset();
								onOpenChange(false);
							}}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={!canApprove || loading}>
							{loading ? 'Approving…' : 'Approve & invite'}
						</Button>
					</DialogFooter>
				</DialogForm>
			</DialogContent>
		</Dialog>
	);
}
